import React from "react";
import { ChevronDown, Check, Globe } from "lucide-react";
import { REGIONS, DEFAULT_REGION, getRegion, regionHref } from "../lib/region";

const FONT_STACK = "'IBM Plex Sans', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif";

// public/accessibility.css gives every link and button a 48px tap target, centred content,
// 0.5rem 1rem padding and (inside <nav>) an 8px margin, with more specificity than utility
// classes. Layout that must differ is therefore set inline.
const INLINE_LINK_STYLE = { margin: 0, padding: 0, minWidth: 0 };
const ROW_LINK_STYLE = {
  display: "flex",
  justifyContent: "flex-start",
  width: "100%",
  margin: 0,
  padding: "10px 12px",
  textDecoration: "none",
};

/** Region the control should show as current: explicit prop, then the address bar, then Singapore. */
function resolveRegion(regionProp) {
  if (regionProp && REGIONS[regionProp]) return regionProp;
  return getRegion() || DEFAULT_REGION;
}

/**
 * Region indicator + switcher.
 *
 * Shows the region of the site being viewed (flag + name). Every destination is a plain
 * <a href> built with regionHref(): each region is its own router, so switching region is
 * always a full page load, never a React Router navigation.
 *
 * variant="menu"   compact button that opens a small menu (desktop header)
 * variant="list"   always-expanded list (mobile drawer, where a popover would be clipped)
 * variant="inline" one line of links (footer)
 */
export default function RegionSwitcher({ region: regionProp, variant = "menu", className = "" }) {
  const current = resolveRegion(regionProp);
  const currentRegion = REGIONS[current];
  const others = Object.values(REGIONS).filter((r) => r.code !== current);

  if (variant === "inline" && regionProp === "all") {
    // Region-neutral pages (the chooser at "/", /about, /blog): both regions are links and
    // "All regions" is the current page.
    return (
      <nav
        aria-label="Region"
        data-testid="region-switcher-inline"
        className={`flex flex-wrap items-center gap-x-3 gap-y-1 text-[12px] text-gray-600 ${className}`}
      >
        {Object.values(REGIONS).map((r) => (
          <React.Fragment key={r.code}>
            <a
              href={regionHref(r.code)}
              className="inline-flex items-center gap-1.5 text-gray-600 hover:text-gray-900 transition-colors duration-200"
              style={INLINE_LINK_STYLE}
            >
              {r.name}
            </a>
            <span aria-hidden="true" className="text-gray-300">
              |
            </span>
          </React.Fragment>
        ))}
        <span className="inline-flex items-center gap-1.5 text-gray-700" aria-current="true">
          <span>
            <span className="sr-only">Current: </span>
            All regions
          </span>
        </span>
      </nav>
    );
  }

  if (variant === "inline") {
    return (
      <nav
        aria-label="Region"
        data-testid="region-switcher-inline"
        className={`flex flex-wrap items-center gap-x-3 gap-y-1 text-[12px] text-gray-600 ${className}`}
      >
        <span className="inline-flex items-center gap-1.5 text-gray-700" aria-current="true">
          <span>
            <span className="sr-only">Current region: </span>
            {currentRegion.name}
          </span>
        </span>
        {others.map((r) => (
          <React.Fragment key={r.code}>
            <span aria-hidden="true" className="text-gray-300">
              |
            </span>
            <a
              href={regionHref(r.code)}
              className="inline-flex items-center gap-1.5 text-gray-600 hover:text-gray-900 transition-colors duration-200"
              style={INLINE_LINK_STYLE}
            >
              {r.name}
            </a>
          </React.Fragment>
        ))}
        <span aria-hidden="true" className="text-gray-300">
          |
        </span>
        <a
          href="/"
          className="text-gray-600 hover:text-gray-900 transition-colors duration-200"
          style={INLINE_LINK_STYLE}
        >
          All regions
        </a>
      </nav>
    );
  }

  if (variant === "list") {
    return (
      <nav aria-label="Region" data-testid="region-switcher-list" className={className}>
        <p
          className="px-4 text-xs uppercase m-0"
          style={{ color: "#60A5FA", opacity: 0.6, fontFamily: FONT_STACK, letterSpacing: "0.08em", fontWeight: 500 }}
        >
          Region
        </p>
        <ul className="mt-1 space-y-1 m-0 p-0 list-none">
          <li>
            <span
              aria-current="true"
              className="flex items-center justify-between gap-2 px-4 py-3 text-base rounded-lg min-h-[48px]"
              style={{ color: "#60A5FA", fontFamily: FONT_STACK, fontWeight: 400, backgroundColor: "rgba(96, 165, 250, 0.1)" }}
            >
              <span className="flex items-center gap-2">
                {currentRegion.name}
              </span>
              <Check size={16} aria-hidden="true" />
            </span>
          </li>
          {others.map((r) => (
            <li key={r.code}>
              <a
                href={regionHref(r.code)}
                className="flex items-center gap-2 px-4 py-3 text-base rounded-lg min-h-[48px] focus-ring"
                style={{ ...ROW_LINK_STYLE, padding: "12px 16px", color: "#60A5FA", fontFamily: FONT_STACK, fontWeight: 300 }}
              >
                {r.name}
              </a>
            </li>
          ))}
          <li>
            <a
              href="/"
              className="flex items-center gap-2 px-4 py-3 text-base rounded-lg min-h-[48px] focus-ring"
              style={{ ...ROW_LINK_STYLE, padding: "12px 16px", color: "#60A5FA", fontFamily: FONT_STACK, fontWeight: 300 }}
            >
              All regions
            </a>
          </li>
        </ul>
      </nav>
    );
  }

  return <RegionMenu current={currentRegion} others={others} className={className} />;
}

function RegionMenu({ current, others, className }) {
  const [open, setOpen] = React.useState(false);
  const rootRef = React.useRef(null);
  const buttonRef = React.useRef(null);
  const itemRefs = React.useRef([]);
  const menuId = React.useId();

  const items = React.useMemo(
    () => [
      ...others.map((r) => ({ key: r.code, href: regionHref(r.code), label: r.name })),
      { key: "all", href: "/", label: "All regions", flag: null },
    ],
    [others]
  );

  const close = React.useCallback((returnFocus) => {
    setOpen(false);
    if (returnFocus) buttonRef.current?.focus();
  }, []);

  // Outside click / touch closes the menu.
  React.useEffect(() => {
    if (!open) return undefined;
    const onPointer = (event) => {
      if (rootRef.current && !rootRef.current.contains(event.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onPointer);
    document.addEventListener("touchstart", onPointer);
    return () => {
      document.removeEventListener("mousedown", onPointer);
      document.removeEventListener("touchstart", onPointer);
    };
  }, [open]);

  // Opening moves focus to the first destination.
  React.useEffect(() => {
    if (open) itemRefs.current[0]?.focus();
  }, [open]);

  const focusItem = (index) => {
    const count = items.length;
    itemRefs.current[(index + count) % count]?.focus();
  };

  const onMenuKeyDown = (event) => {
    const index = itemRefs.current.findIndex((node) => node === document.activeElement);
    if (event.key === "Escape") {
      event.preventDefault();
      event.stopPropagation();
      close(true);
    } else if (event.key === "ArrowDown") {
      event.preventDefault();
      focusItem(index + 1);
    } else if (event.key === "ArrowUp") {
      event.preventDefault();
      focusItem(index - 1);
    } else if (event.key === "Home") {
      event.preventDefault();
      focusItem(0);
    } else if (event.key === "End") {
      event.preventDefault();
      focusItem(items.length - 1);
    } else if (event.key === "Tab") {
      setOpen(false);
    }
  };

  const onButtonKeyDown = (event) => {
    if (event.key === "ArrowDown" && !open) {
      event.preventDefault();
      setOpen(true);
    } else if (event.key === "Escape" && open) {
      event.preventDefault();
      close(true);
    }
  };

  return (
    <div ref={rootRef} className={`relative shrink-0 ${className}`} data-testid="region-switcher">
      <button
        ref={buttonRef}
        type="button"
        onClick={() => setOpen((value) => !value)}
        onKeyDown={onButtonKeyDown}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-controls={open ? menuId : undefined}
        aria-label={`Region: ${current.name}. Change region`}
        title={`Region: ${current.name}`}
        className="flex items-center gap-1.5 rounded-md px-1.5 py-1 text-sm lg:text-[15px] leading-none whitespace-nowrap transition-colors duration-150 hover:bg-gray-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#60A5FA]/40 focus-visible:ring-offset-2"
        style={{
          fontFamily: FONT_STACK,
          letterSpacing: "0.003em",
          fontWeight: 300,
          color: "#60A5FA",
          backgroundColor: "transparent",
          border: "none",
          boxShadow: "none",
          padding: "4px 6px",
        }}
      >
        <Globe size={14} strokeWidth={1.75} aria-hidden="true" style={{ flexShrink: 0 }} />
        {/* The full region name from the tablet breakpoint up; the two-letter code only on phones. */}
        <span className="hidden md:block">{current.name}</span>
        <span className="md:hidden">{current.code.toUpperCase()}</span>
        <ChevronDown
          aria-hidden="true"
          className={`w-4 h-4 transition-transform duration-200 ${open ? "rotate-180" : ""}`}
          style={{ color: "#60A5FA" }}
        />
      </button>

      {open ? (
        <div
          id={menuId}
          role="menu"
          aria-label="Choose region"
          onKeyDown={onMenuKeyDown}
          className="absolute right-0 top-full mt-2 z-50 w-56 rounded-xl border border-gray-200 overflow-hidden"
          style={{
            backgroundColor: "#FFFFFF",
            boxShadow: "0 8px 32px rgba(0, 0, 0, 0.12), 0 2px 8px rgba(0, 0, 0, 0.08)",
          }}
        >
          <div className="p-2">
            <div
              className="flex items-center justify-between gap-2 px-3 py-2.5 rounded-lg bg-blue-50"
              style={{ fontFamily: FONT_STACK, fontWeight: 400, color: "#60A5FA", fontSize: 14 }}
            >
              <span className="flex items-center gap-2">
                <span>
                  <span className="sr-only">Current region: </span>
                  {current.name}
                </span>
              </span>
              <Check className="w-4 h-4" aria-hidden="true" />
            </div>
            {items.map((item, index) => (
              <a
                key={item.key}
                ref={(node) => {
                  itemRefs.current[index] = node;
                }}
                role="menuitem"
                href={item.href}
                className="flex items-center gap-2 px-3 py-2.5 rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-200"
                style={{
                  ...ROW_LINK_STYLE,
                  fontFamily: FONT_STACK,
                  fontWeight: 300,
                  color: "#60A5FA",
                  fontSize: 14,
                }}
              >
                {item.label}
              </a>
            ))}
          </div>
        </div>
      ) : null}
    </div>
  );
}
